/**
 * THE SCREEN AT `/`, AND THE SHARED FURNITURE EVERY OTHER SCREEN BORROWS.
 *
 * Canon's "System Login page as a permission-filtered menu" is now the left
 * rail, not this file: every destination in it came from fn_my_admin_cards()
 * and none is written down in this repository. A card that renders and then
 * refuses when pressed is worse than a card that never rendered — it teaches
 * staff the menu is unreliable, and it tells somebody exactly which
 * capabilities exist that they do not have. That rule moved with the menu.
 *
 * What is left here is the field onboarding panel, the two gates that stand in
 * front of a person being able to work at all, and the `Page` and
 * `PortalHeader` that CardScreen and MasterDashboard import.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  O, SANS, DISPLAY, Card, Field, IconChip, StatusPill, PrimaryCTA, QuietButton,
  TrustFooter, textInputStyle,
} from './ui';
import { moduleCardsOf, masterCardOf, recordMyStaffIdNumber } from './lib/portal';
import { beginEnrolment, confirmEnrolment, myMfaStanding } from './lib/mfa';

// One glyph per card. Deliberately typographic rather than an icon library:
// the consumer workspace draws its own marks the same way, and a second icon
// system would be a second visual language by the back door.
const MARKS = {
  compliance: '◇',
  internal_audit: '◈',
  legal_disputes: '§',
  finance: 'R',
  business_intel: '◧',
  human_capital: '◉',
  agentic_capital: '◎',
  support_centre: '✆',
  marketing: '❖',
  system_operations: '⚙',
  developer_console: '⌘',
  orchid_insights: '◐',
  orchid_developments: '⌂',
};

export function PortalHeader({ staff, onSignOut, children }) {
  return (
    <header style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end',
      justifyContent: 'space-between', gap: 16, marginBottom: 26,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
        <img
          src="/images/orchid-logo-green.png"
          alt=""
          width={40}
          height={40}
          style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          onError={(e) => { e.target.style.visibility = 'hidden'; }}
        />
      <div>
        {/* "Any reference to the portal from within the portal must not have
            'staff' anywhere; it must simply say Orchid Portal. Naming the
            person signed in and their email address - that we can keep." */}
        <div style={{ fontFamily: DISPLAY, fontSize: 22, color: O.green }}>Orchid Portal</div>
        {children}
        {staff && (
          <p style={{ marginTop: 10, fontSize: 13, color: O.inkMuted }}>
            {staff.full_name}{staff.email ? ` · ${staff.email}` : ''}
          </p>
        )}
      </div>
      </div>
      {onSignOut && (
        <div style={{ width: 120 }}>
          <QuietButton onClick={onSignOut}>Sign out</QuietButton>
        </div>
      )}
    </header>
  );
}

export const Page = ({ children, wide = false }) => (
  <main style={{
    maxWidth: wide ? 1040 : 860, margin: '0 auto',
    padding: '36px 20px 56px', fontFamily: SANS, color: O.ink,
  }}>{children}</main>
);

/**
 * THE ORANGE DOT.
 *
 *   "if any new task or notification comes up, there's no way to tell it's
 *    there unless i manually go to the specific card I'm expecting something.
 *    we should probably add an orange dot anywhere there's a new task."
 *
 * Orange because that is already what an unanswered thing looks like on this
 * platform -- the consumer workspace's message icon uses the same colour for
 * the same meaning, and a second colour for "something is waiting" would be a
 * second language.
 *
 * IT CARRIES THE NUMBER, not just the dot. "Compliance has something" sends
 * somebody to look; "Compliance has 14" tells them whether to look now. The
 * count costs nothing extra -- the database returned it either way -- and a
 * bare dot would be throwing away the only part that helps them choose.
 */
const AttentionDot = ({ waiting }) => {
  if (!waiting) return null;
  return (
    <span
      // A screen reader gets the sentence rather than the shape.
      aria-label={`${waiting} waiting`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 20, height: 20, padding: '0 6px', borderRadius: 999,
        background: '#D68910', color: '#2A1B02',
        fontFamily: SANS, fontSize: 11, fontWeight: 700,
      }}
    >{waiting > 99 ? '99+' : waiting}</span>
  );
};

function ModuleCard({ card, waiting = 0 }) {
  return (
    <Link to={`/card/${card.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <IconChip>{MARKS[card.slug] || '◇'}</IconChip>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <AttentionDot waiting={waiting} />
            <StatusPill tone="quiet">
              {card.permissions_held} {card.permissions_held === 1 ? 'permission' : 'permissions'}
            </StatusPill>
          </div>
        </div>
        {/* "remove the descriptions beneath card's name; just let it say, for
            instance, 'Developer Console,' and nothing else. Only have the
            card's description AFTER the permissioned individual has clicked on
            that card and is on that card's screen." */}
        <h2 style={{ marginTop: 13, fontSize: 15, fontWeight: 700, color: O.ink }}>{card.title}</h2>
      </Card>
    </Link>
  );
}

/**
 * FOR EVERYBODY WHO WAS ALREADY HERE.
 *
 *   "she has an oid already. now the question: will she get the same oid when I
 *    add her again? because i don't think the old one is linked to her id
 *    number hash."
 *
 * She was right, and it was true of every staff member on the database: an OID
 * was five random digits belonging to a ROW, so deleting the row left the code
 * referring to nobody. Script 95 fixed that at the door people come IN by —
 * which would have meant the rule applied only to people hired after it
 * shipped, and the founder's own OID stayed a property of a record.
 *
 * This is the other half. It appears once, for somebody whose OID is not yet
 * tied to them, and it goes away for good the moment it is.
 */
function AnchorYourOid({ staff }) {
  const [idNumber, setIdNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [message, setMessage] = useState(null);

  const submit = async () => {
    if (!idNumber.trim()) {
      setFailure('An ID number, so the code is tied to you rather than to this record.');
      return;
    }
    setBusy(true);
    setFailure(null);
    const { data, error } = await recordMyStaffIdNumber(idNumber);
    setBusy(false);
    if (error) { setFailure(error); return; }
    // SAID, AND LEFT ON SCREEN.
    //
    // Re-reading the account here would unmount this panel the instant it had
    // something to say — has_id_on_file becomes true, the condition that
    // renders it goes false, and the confirmation disappears in the same frame
    // it appeared. Nothing else on this portal reads staff.oid_admin_id, so
    // there is nothing stale to correct; the message carries the OID that came
    // back, which is the only value that could have changed.
    setMessage(data?.message || 'Recorded.');
  };

  if (message) {
    return (
      <div style={{ marginBottom: 20 }}>
        <Card style={{ borderColor: 'rgba(13,61,42,0.30)', background: 'rgba(13,61,42,0.04)' }}>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: O.ink }}>{message}</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <Card>
        <p style={{ fontSize: 13.5, fontWeight: 700, color: O.ink }}>
          {staff.oid_admin_id ? `${staff.oid_admin_id} is not yet tied to you.` : 'Your OID is not yet tied to you.'}
        </p>
        <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
          It belongs to this record rather than to the person. Give your ID number once
          and it becomes yours permanently: if you ever leave Orchid and come back, you
          are recognised and handed the same code instead of a new one. It is held as a
          one-way hash — Orchid keeps the ability to tell that two records are the same
          person, and nothing else.
        </p>
        <div style={{ marginTop: 12 }}>
          <Field label="Your ID number" htmlFor="anchor-id-number">
            <input
              id="anchor-id-number"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              style={textInputStyle(false)}
            />
          </Field>
        </div>
        {failure && (
          <p style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.6, color: O.critical }}>
            {failure}
          </p>
        )}
        <div style={{ marginTop: 14, maxWidth: 220 }}>
          <PrimaryCTA onClick={submit} busy={busy} disabled={busy}>
            {busy ? 'Recording…' : 'Make it permanent'}
          </PrimaryCTA>
        </div>
      </Card>
    </div>
  );
}

/**
 * ENROLMENT, WHERE THE WARNING USED TO BE.
 *
 *   "the MFA business: nobody is set up for it, not even me. how can we set it
 *    up in such a way that it costs nothing?"
 *
 * What stood here was a panel explaining that permissions requiring MFA could
 * not be granted to this account until enrolment was complete — over a portal
 * where enrolment was not possible. admin_users.mfa_enrolled was written by
 * nothing anywhere in the codebase, so it was false for every account since the
 * day it was added, every requires_mfa permission was ungrantable to anybody,
 * and the only sign of it was a paragraph that read as a to-do.
 *
 * It costs nothing because TOTP is part of Supabase Auth on every plan: a
 * secret Supabase generates, a code an authenticator app produces from it, and
 * no message sent to anybody. SMS factors are the ones that are billed, and
 * script 98 refuses to count them for exactly that reason.
 *
 * NOTHING HERE TELLS THE DATABASE ANYTHING. confirmEnrolment verifies the code
 * with Supabase and then asks the database to LOOK at auth.mfa_factors. A
 * screen that set the column would have been the original bug with a nicer
 * surface, and script 98 refuses it from any signed-in session in any case.
 */
function SetUpMfa({ staff }) {
  /**
   * WHY THIS READS THE DATABASE RATHER THAN THE staff ROW IT WAS HANDED.
   *
   *   "when the mfa is removed, the staff member must get the orange
   *    authenticator setup box on their screen UNTIL they have set it up again."
   *
   * staff.mfa_enrolled is a cache, refreshed when its owner signs in. Somebody
   * whose authenticator was removed by Human Capital ten minutes ago is still
   * carrying `true` in that cache until their next sign-in — which is exactly
   * the window this box exists for, so reading it would hide the box precisely
   * when it is needed.
   *
   * fn_my_mfa_standing reads auth.mfa_factors. It also says WHO removed the
   * last one and WHEN, because an orange box appearing out of nowhere reads as
   * a fault, and the same box naming a colleague and a date reads as something
   * that happened for a reason.
   */
  const [standing, setStanding] = useState(null);

  useEffect(() => {
    let alive = true;
    myMfaStanding().then(({ data }) => { if (alive) setStanding(data); });
    return () => { alive = false; };
  }, []);
  const [step, setStep] = useState('idle');   // idle | scanning | done
  const [enrolment, setEnrolment] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [message, setMessage] = useState(null);

  const start = async () => {
    setBusy(true); setFailure(null);
    const { data, error } = await beginEnrolment(`Orchid Portal — ${staff.email}`);
    setBusy(false);
    if (error) { setFailure(error); return; }
    setEnrolment(data);
    setStep('scanning');
  };

  const finish = async () => {
    setBusy(true); setFailure(null);
    const { data, error } = await confirmEnrolment(enrolment?.factorId, code);
    setBusy(false);
    if (error) { setFailure(error); return; }
    setMessage(data?.message || 'Multi-factor authentication is on for this account.');
    setStep('done');
  };

  if (step === 'done') {
    return (
      <div style={{ marginBottom: 20 }}>
        <Card style={{ borderColor: 'rgba(13,61,42,0.30)', background: 'rgba(13,61,42,0.04)' }}>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: O.ink }}>{message}</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <Card style={{ background: 'rgba(214,137,16,0.06)', border: '1px solid rgba(214,137,16,0.28)' }}>
        <p style={{ fontSize: 13.5, fontWeight: 700, color: '#7A4E06' }}>
          {standing?.was_reset
            ? 'Your authenticator was removed. Set up a new one.'
            : 'This account has no authenticator set up.'}
        </p>
        {standing?.was_reset && (
          <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: '#7A4E06' }}>
            {standing.reset_by_name
              ? <>Removed by <strong>{standing.reset_by_name}</strong></>
              : 'Removed by Human Capital'}
            {standing.reset_at
              ? <> on {new Date(standing.reset_at).toLocaleDateString('en-ZA', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}.</>
              : '.'}{' '}
            Scan the new code below on whichever phone you are using now.
          </p>
        )}
        <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: '#7A4E06' }}>
          Permissions that open identity documents, move money, change a score or manage
          staff cannot be granted to it until one is — the database refuses the grant, not
          the screen. Setting one up takes a minute and costs nothing: it is a six-digit
          code from an app on your phone, with no SMS and no subscription.
        </p>

        {step === 'idle' && (
          <div style={{ marginTop: 14, maxWidth: 260 }}>
            <PrimaryCTA onClick={start} busy={busy} disabled={busy}>
              {busy ? 'Preparing…' : 'Set up my authenticator'}
            </PrimaryCTA>
          </div>
        )}

        {step === 'scanning' && (
          <div style={{ marginTop: 14 }}>
            <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#7A4E06' }}>
              Open Google Authenticator, Microsoft Authenticator, 1Password or any app
              that does the same job, and scan this. Then type the six digits it shows.
            </p>
            {enrolment?.qr && (
              <img
                src={enrolment.qr}
                alt="Scan this with your authenticator app"
                width={168}
                height={168}
                style={{ marginTop: 12, background: '#fff', borderRadius: 10, padding: 8 }}
              />
            )}
            {/* Shown alongside the QR because a phone cannot scan the screen it is
                standing next to, and typing the key beats not enrolling. */}
            {enrolment?.secret && (
              <p style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.6, color: '#7A4E06' }}>
                Cannot scan? Enter this key by hand:{' '}
                <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{enrolment.secret}</code>
              </p>
            )}
            <div style={{ marginTop: 12, maxWidth: 220 }}>
              <Field label="Six-digit code" htmlFor="mfa-enrol-code">
                <input
                  id="mfa-enrol-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !busy) finish(); }}
                  style={{ ...textInputStyle(false), letterSpacing: 4, fontSize: 18 }}
                />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <div style={{ width: 110 }}>
                <QuietButton onClick={() => { setStep('idle'); setCode(''); setFailure(null); }}>
                  Cancel
                </QuietButton>
              </div>
              <div style={{ width: 190 }}>
                <PrimaryCTA onClick={finish} busy={busy} disabled={busy}>
                  {busy ? 'Checking…' : 'Turn it on'}
                </PrimaryCTA>
              </div>
            </div>
            {/* Said here rather than after it has happened. */}
            <p style={{ marginTop: 12, fontSize: 11.5, lineHeight: 1.6, color: '#7A4E06' }}>
              Once this is on you will be asked for a code every time you sign in. If you
              lose the phone, the authenticator has to be removed from the Supabase
              dashboard by whoever owns the project — nothing in this portal can clear it.
            </p>
          </div>
        )}

        {failure && (
          <p style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.6, color: O.critical }}>
            {failure}
          </p>
        )}
      </Card>
    </div>
  );
}

/**
 * THE OVERVIEW SCREEN, BACK — AND WITHOUT THE FIELD ONBOARDING PANEL.
 *
 *   "you can put it back, but just remove the Field Onboarding part from it,
 *    because that has its own screen now."
 *    -- the founder, 13 September 2026
 *
 * It was removed this morning on the reasoning that the grid and the rail were
 * one answer drawn twice. That was true of the CARDS and it was never true of
 * the panel: field onboarding was the thing this screen carried that the rail
 * could not, which is why the screen stayed behind when the grid went. Now the
 * panel has an address of its own, so the two swap places — the grid comes back
 * and the panel leaves.
 *
 * WHAT THIS SUPERSEDES, SAID RATHER THAN DROPPED. An earlier standing ruling
 * read: "as soon as someone signs in and inputs their authenticator code, the
 * field onboarding section must be the first thing they see." With the panel on
 * its own screen and Overview at the front door, that is no longer literally
 * true. It is superseded by the instruction above rather than forgotten, and it
 * is one line in lib/rail.js — the order of two entries — to put field
 * onboarding at the top of the rail again if that ruling should still hold.
 *
 * THE TWO GATES STAY, because they were never cards either and there is still
 * nowhere else for them: the ID-number gate is the only place an OID gets tied
 * to a person, and the MFA gate is the only route anybody has to enrolling.
 */
export default function Portal({ cards, staff, onSignOut, attention = {} }) {
  const modules = moduleCardsOf(cards);
  const master = masterCardOf(cards);

  return (
    <Page wide>
      <PortalHeader staff={staff} onSignOut={onSignOut} />

      {/* has_id_on_file is false only when the database answered and said so.
          undefined means the portal is running against a database without
          script 95, and asking for an ID number that nothing could store would
          be a form with nowhere to go. */}
      {staff && staff.has_id_on_file === false && <AnchorYourOid staff={staff} />}

      {/*
        MFA IS NOT A NAG, AND IT IS NO LONGER ONLY A WARNING.
        Script 49 refuses an MFA-required permission to an unenrolled account on
        BOTH grant paths, so "you will not be given X until you enrol" is a fact
        about the database. What was missing was any way to enrol: the column it
        reads was written by nothing at all until script 98.
      */}
      {staff && !staff.mfa_enrolled && <SetUpMfa staff={staff} />}

      {master && (
        <div style={{ marginBottom: 20 }}>
          <Link to="/master" style={{ textDecoration: 'none' }}>
            {/* Deep green, not gold. "Let's stick to deep green, grey, and
                off-white for the general screens' colours ... no gold." Gold
                stays what canon reserves it for -- status that was earned --
                and holding the master grant is an assignment, not an
                achievement. */}
            <Card style={{ borderColor: 'rgba(13,61,42,0.30)', background: 'rgba(13,61,42,0.04)' }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: O.ink }}>{master.title}</h2>
            </Card>
          </Link>
        </div>
      )}

      {modules.length === 0 && (
        <Card style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 14, color: O.ink }}>This account holds no permissions yet.</p>
          <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            Cards appear here as permissions are granted from People &amp; Access. An
            empty portal is a complete and correct answer, not a fault.
          </p>
          {/* AND THE ONE THING THEY CAN ALREADY DO IS SAID HERE. Somebody
              reading this sentence on their first morning holds nothing — and
              the welcome pack that explains what Orchid is was written for
              exactly them. It sits in the grid below either way; naming it
              here is what stops "you have nothing" reading as "there is
              nothing". */}
          <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            Resources, below, is open to you now — the Orchid Welcome Pack and every
            procedure the company has published are in it.
          </p>
        </Card>
      )}

      <div style={{
        display: 'grid', gap: 14,
        gridTemplateColumns: 'repeat(auto-fill, minmax(258px, 1fr))',
      }}>
        {modules.map((c) => <ModuleCard key={c.slug} card={c} waiting={attention[c.slug] || 0} />)}

        {/*
          THE ONE LAST CARD.

            "we should probably add one last card to the Overview screen of the
             Portal: Resources. That's where every single company document can
             be found - SOPs, departmental Welcome Packs, HR resources,
             processes and procedures, etc."

          IN THE GRID AND NOT ABOVE IT, because he called it a card and it
          should read as one. But it is NOT a row in admin_cards and it does not
          come from fn_my_admin_cards(): `portal_documents` is readable by every
          active member of staff and by no permission at all, so a card — which
          is drawn only for somebody holding a permission inside it — would put
          a lock on the outside of an open room.

          It carries no permission count for the same reason. A count of zero
          would be true and would read as "you may do nothing here", which is
          the opposite of what is true: everybody may.
        */}
        <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Card>
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
            }}>
              <IconChip>▤</IconChip>
              <StatusPill tone="quiet">Everybody</StatusPill>
            </div>
            <h2 style={{ marginTop: 13, fontSize: 15, fontWeight: 700, color: O.ink }}>Resources</h2>
          </Card>
        </Link>
      </div>

      {/* The append-only-log sentence that used to close this screen is gone
          on the founder's ruling. The guarantee is unchanged -- script 48's
          triggers still refuse an edit or a delete from every role including
          super_admin -- it simply no longer needs restating on the menu. */}
      <TrustFooter />
    </Page>
  );
}
